// Is this product a saree?
//
// The Authenticity Promise covers sarees only, so every surface that makes the claim has to answer
// this question the same way. One module, so the home banner, the PDP badge and anything added
// later cannot drift apart.
//
// ⚠️ MATCH ON NAME, NEVER ON ID. `health_concern_type.hct_id` is an IDENTITY column and its value
// differs per environment — "Sarees" is 229 on dev and there is no guarantee it is 229 on staging
// or prod. The backend hit this exact problem and solved it the same way: DashboardService's
// Newly-Arrived card matches `LOWER(TRIM(hct.hct_name)) = 'sarees'` and its comment explains that
// an hct_id list "was rejected instead because ids are IDENTITY and differ per environment".
// This is the frontend half of that decision.
//
// EQUALITY, not a substring match — again following the backend. A `includes("saree")` test would
// also match undergarment categories like "Saree Shapewear" and "Petticoats & Saree Shapewear",
// which are emphatically not sarees and must never carry the promise. Case and surrounding
// whitespace vary freely in the data, which is what the lowercase + trim are for.
//
// Verified against the live dev API (2026-09-19), not assumed:
//   /product/open/get_by_id/735 -> productHealthConcernTypes: [{id:229, name:"Sarees", status:1}]
//   /product/open/get_by_id/1   -> productHealthConcernTypes: null
// The null case is real and common, which is why every access below is defensive.
//
// TWO PAYLOAD SHAPES, one gate. The PDP sends objects; the cart sends plain strings:
//   PDP   ProductData.productHealthConcernTypes -> [{ id: 229, name: "Sarees", status: 1 }]
//   cart  CartItemDTO.categoryNames             -> ["Sarees"]
// Both are the same health_concern_type taxonomy and both must answer this question identically,
// so the shape is normalised here rather than at two call sites that could drift apart.
//
// ⚠️ ALWAYS A LIST, NEVER A SINGLE VALUE — on either surface. A product commonly carries several
// categories (on dev, 309 of 627 categorised products carry two or more), so a saree that also
// carries "Handloom" must still pass. Anything that reduced these to one value before asking
// would silently drop the promise from exactly those products.

// The one canonical spelling, already lowercased and trimmed for comparison.
export const SAREE_CATEGORY_NAME = "sarees";

// The "Sarees under ₹2,000" offer: the home banner links to the Sarees listing with
// ?under=2000. One constant so the banner's copy and its link can never disagree. INR only: listing prices arrive in the
// shopper's display currency, so 2000 would mean $2,000 to an international shopper.
export const SAREES_UNDER_PRICE = 2000;

// URL param for a HIDDEN price cap. Unlike price=-2000 it never shows in the filter UI (no
// "Under ₹2,000" tag, no Price · 1, no 2000 in Max) and "Clear all" does not lift it: the shopper
// sees just the offer's sarees and leaves with Back. The listing still sends it as maxPrice.
export const SAREES_UNDER_PARAM = "under";

// Read the hidden cap from the query string: a positive number, else null.
export const parseUnderParam = (searchParams) => {
  const n = Number(searchParams.get(SAREES_UNDER_PARAM));
  return Number.isFinite(n) && n > 0 ? n : null;
};

// Is this sub-category NAME the Sarees category? Same rule as isSareeProduct (equality after
// trim + lowercase, never a substring), for callers that hold one name rather than a list.
export const isSareeCategoryName = (name) =>
  typeof name === "string" && name.trim().toLowerCase() === SAREE_CATEGORY_NAME;

// Pull the comparable name out of either shape. Anything else yields null and cannot match.
function categoryNameOf(entry) {
  if (typeof entry === "string") return entry;
  if (entry && typeof entry.name === "string") return entry.name;
  return null;
}

/**
 * @param {Array<{name:string}|string>|null|undefined} categories
 *        Either the PDP's `productHealthConcernTypes` (objects) or the cart item's
 *        `categoryNames` (strings). Null/undefined/anything else is simply not a saree.
 * @returns {boolean} true only when one of the product's categories IS "Sarees".
 */
export function isSareeProduct(categories) {
  if (!Array.isArray(categories)) return false;

  return categories.some((entry) => {
    const name = categoryNameOf(entry);
    return (
      typeof name === "string" &&
      name.trim().toLowerCase() === SAREE_CATEGORY_NAME
    );
  });
}
